import { useCallback, useEffect, useRef } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import useNavTopBar from "@organisms/NavTopBar/NavTopBar.context"

interface UseNavTopBarMobileTabsReturn {
    mobileTabsRef: (element: HTMLDivElement | null) => void
}

const MOBILE_TABS_SNAP_DELAY_MS = 150

const TABS_OFFSET_PROPERTY = "--nav-top-bar-mobile-tabs-offset"
const TABS_DURATION_PROPERTY = "--nav-top-bar-mobile-tabs-duration"

/**
 * Hide the mobile tabs bar while the page scrolls down and reveal it when the page scrolls back up.
 *
 * The offset is published as a custom property on the sticky top bar: the bar slides up by that amount so the
 * content below reclaims the space, while the header counter-translates to stay pinned and the tabs slide behind
 * it. Setting the offset on the bar's height alone would leave the vacated band covering the content.
 */
export default function useNavTopBarMobileTabs(): UseNavTopBarMobileTabsReturn {
    const { topBarRef, setMobileTabsRef } = useNavTopBar()

    const isDesktop = useIsDesktop()

    const translateYRef = useRef(0)
    const tabsNodeRef = useRef<HTMLDivElement | null>(null)

    const mobileTabsRef = useCallback(
        (element: HTMLDivElement | null) => {
            tabsNodeRef.current = element
            setMobileTabsRef(element)
        },
        [setMobileTabsRef],
    )

    useEffect(() => {
        if (isDesktop || !topBarRef) {
            translateYRef.current = 0
            topBarRef?.style.removeProperty(TABS_OFFSET_PROPERTY)
            topBarRef?.style.removeProperty(TABS_DURATION_PROPERTY)

            return
        }

        let lastScrollY = window.scrollY
        let snapTimeout: ReturnType<typeof setTimeout>

        const applyOffset = (translateY: number, isAnimated: boolean) => {
            translateYRef.current = translateY
            topBarRef.style.setProperty(TABS_DURATION_PROPERTY, isAnimated ? "var(--duration-moderate)" : "0s")
            topBarRef.style.setProperty(TABS_OFFSET_PROPERTY, `${translateY}px`)
        }

        const handleScroll = () => {
            const tabsHeight = tabsNodeRef.current?.offsetHeight ?? 0

            if (tabsHeight === 0) {
                return
            }

            const currentScrollY = window.scrollY
            const scrollDelta = currentScrollY - lastScrollY

            lastScrollY = currentScrollY

            clearTimeout(snapTimeout)

            applyOffset(Math.max(-tabsHeight, Math.min(0, translateYRef.current - scrollDelta)), false)

            snapTimeout = setTimeout(() => {
                applyOffset(translateYRef.current < -tabsHeight / 2 ? -tabsHeight : 0, true)
            }, MOBILE_TABS_SNAP_DELAY_MS)
        }

        window.addEventListener("scroll", handleScroll, { passive: true })

        return () => {
            window.removeEventListener("scroll", handleScroll)
            clearTimeout(snapTimeout)
        }
    }, [isDesktop, topBarRef])

    return {
        mobileTabsRef,
    }
}
